import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Car,
  Euro,
  Calendar,
  Gauge,
  Fuel,
  Settings2,
  MapPin,
  Palette,
  Zap,
  ChevronLeft,
  ChevronRight,
  X,
  Check,
  Phone,
  MessageCircle,
  ArrowLeft,
  Star,
  Image as ImageIcon,
  Send,
  User,
  Mail,
  Loader2,
  Package,
  Share2,
  Copy,
  Facebook,
  MessageSquare,
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AvailableVehiclesPage = () => {
  const navigate = useNavigate();
  const { vehicleId } = useParams();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageGalleryOpen, setImageGalleryOpen] = useState(false);
  
  // Contact form state
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    prestation: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Prestations disponibles
  const PRESTATIONS = [
    { value: "", label: "Sélectionnez une prestation" },
    { value: "pack-essentiel", label: "Pack Essentiel - 990€", description: "Recherche + Contrôle + Accompagnement" },
    { value: "pack-avance", label: "Pack Avancé - 1 290€", description: "Essentiel + Négociation + Livraison" },
    { value: "pack-securite", label: "Pack Sécurité - 2 490€", description: "Avancé + Garantie 12 mois" },
    { value: "pack-premium", label: "Pack Premium - 3 990€", description: "All inclusive + Conciergerie" },
    { value: "import-coree", label: "Import Corée - 3 990€", description: "Import complet depuis la Corée du Sud" },
    { value: "indecis", label: "Je ne sais pas encore", description: "Je souhaite être conseillé" },
  ];

  useEffect(() => {
    document.title = "Véhicules Disponibles à la Commande | Fast&Car - Import Automobile";
    
    // SEO meta
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = "Découvrez notre sélection de véhicules premium disponibles à la commande. Import d'Allemagne, Belgique et Corée du Sud. BMW, Audi, Mercedes et plus.";

    fetchVehicles();
  }, []);

  // Auto-open detail modal when URL contains a vehicleId (deep-linking)
  useEffect(() => {
    if (!vehicleId || vehicles.length === 0) return;
    const v = vehicles.find((x) => x.id === vehicleId);
    if (v) {
      setSelectedVehicle(v);
      setCurrentImageIndex(0);
      setShowContactForm(false);
      setContactForm({ name: "", email: "", phone: "", prestation: "", message: "" });
      setDetailOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId, vehicles]);

  // Update page title + OG meta when a vehicle is selected
  useEffect(() => {
    if (!selectedVehicle) return;
    const title = `${selectedVehicle.brand} ${selectedVehicle.model} ${selectedVehicle.year} - ${formatPrice(selectedVehicle.price)} | Fast&Car`;
    document.title = title;

    const setMeta = (property, content, attr = "property") => {
      let tag = document.querySelector(`meta[${attr}="${property}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, property);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };
    const img = selectedVehicle.images?.[0] || "";
    const desc = selectedVehicle.description || `${selectedVehicle.brand} ${selectedVehicle.model} ${selectedVehicle.year} importée par Fast&Car.`;
    const url = `${window.location.origin}/vehicules-disponibles/${selectedVehicle.id}`;

    setMeta("og:title", title);
    setMeta("og:description", desc);
    setMeta("og:image", img);
    setMeta("og:url", url);
    setMeta("og:type", "product");
    setMeta("twitter:card", "summary_large_image", "name");
    setMeta("twitter:title", title, "name");
    setMeta("twitter:description", desc, "name");
    setMeta("twitter:image", img, "name");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVehicle]);

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API}/available-vehicles`);
      setVehicles(response.data);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const openVehicleDetail = (vehicle) => {
    setSelectedVehicle(vehicle);
    setCurrentImageIndex(0);
    setShowContactForm(false);
    setContactForm({ name: "", email: "", phone: "", prestation: "", message: "" });
    setDetailOpen(true);
    // Update URL for deep-linking/sharing (no page reload)
    if (vehicle?.id) {
      window.history.replaceState(null, "", `/vehicules-disponibles/${vehicle.id}`);
      // Track view (fire and forget)
      axios.post(`${API}/available-vehicles/${vehicle.id}/track`, { event: "view" }).catch(() => {});
    }
  };

  const closeVehicleDetail = (open) => {
    setDetailOpen(open);
    if (!open) {
      window.history.replaceState(null, "", "/vehicules-disponibles");
    }
  };

  const getShareUrl = (vehicle) =>
    `${window.location.origin}/vehicules-disponibles/${vehicle.id}`;

  const getShareText = (vehicle) =>
    `${vehicle.brand} ${vehicle.model} ${vehicle.year} - ${formatPrice(vehicle.price)} | Fast&Car`;

  const trackShare = (vehicleId, channel) => {
    axios.post(`${API}/available-vehicles/${vehicleId}/track`, { event: "share", channel }).catch(() => {});
  };

  const handleShare = async (vehicle, e) => {
    if (e) e.stopPropagation();
    const url = getShareUrl(vehicle);
    const text = getShareText(vehicle);
    if (navigator.share) {
      try {
        await navigator.share({ title: text, text, url });
        trackShare(vehicle.id, "native");
        return;
      } catch (err) {
        if (err.name === "AbortError") return;
      }
    }
    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(url);
      trackShare(vehicle.id, "copy");
      toast.success("Lien copié !");
    } catch {
      toast.error("Impossible de copier le lien");
    }
  };

  const copyLink = async (vehicle) => {
    const url = getShareUrl(vehicle);
    try {
      await navigator.clipboard.writeText(url);
      trackShare(vehicle.id, "copy");
      toast.success("Lien copié dans le presse-papier");
    } catch {
      toast.error("Échec de la copie");
    }
  };

  const shareWhatsApp = (vehicle) => {
    const text = `${getShareText(vehicle)}\n${getShareUrl(vehicle)}`;
    trackShare(vehicle.id, "whatsapp");
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const shareFacebook = (vehicle) => {
    const url = encodeURIComponent(getShareUrl(vehicle));
    trackShare(vehicle.id, "facebook");
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, "_blank");
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactForm.name || !contactForm.phone) {
      toast.error("Veuillez remplir au moins votre nom et téléphone");
      return;
    }
    
    setSubmitting(true);
    
    try {
      const vehicleInfo = `${selectedVehicle.brand} ${selectedVehicle.model} ${selectedVehicle.year} - ${formatPrice(selectedVehicle.price)}`;
      const prestationLabel = PRESTATIONS.find(p => p.value === contactForm.prestation)?.label || "Non précisée";
      
      await axios.post(`${API}/leads`, {
        name: contactForm.name,
        email: contactForm.email || `${contactForm.phone.replace(/\s/g, "")}@vehicule-interet.com`,
        phone: contactForm.phone,
        source: "contact",
        project_type: selectedVehicle.origin === "Corée du Sud" ? "korea" : "europe",
        vehicle_type: `${selectedVehicle.brand} ${selectedVehicle.model}`,
        message: `🚗 Véhicule: ${vehicleInfo}\n📦 Prestation souhaitée: ${prestationLabel}\n\n${contactForm.message || "Je souhaite avoir plus d'informations."}`,
        budget: formatPrice(selectedVehicle.price),
        prestation: contactForm.prestation,
      });
      
      toast.success("Votre demande a été envoyée ! Nous vous recontactons rapidement.");
      setShowContactForm(false);
      setContactForm({ name: "", email: "", phone: "", prestation: "", message: "" });
      // Track contact event
      axios.post(`${API}/available-vehicles/${selectedVehicle.id}/track`, { event: "contact" }).catch(() => {});
    } catch (error) {
      console.error("Error submitting contact:", error);
      toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
    } finally {
      setSubmitting(false);
    }
  };

  const nextImage = () => {
    if (selectedVehicle?.images?.length > 0) {
      setCurrentImageIndex((prev) =>
        prev === selectedVehicle.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = () => {
    if (selectedVehicle?.images?.length > 0) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? selectedVehicle.images.length - 1 : prev - 1
      );
    }
  };

  const featuredVehicles = vehicles.filter((v) => v.is_featured);
  const otherVehicles = vehicles.filter((v) => !v.is_featured);

  return (
    <div data-testid="available-vehicles-page" className="pt-24 md:pt-32 min-h-screen bg-zinc-950">
      {/* Hero Section */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-neon-lime/5 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="font-unbounded text-3xl md:text-5xl font-bold text-white mb-6">
              Véhicules <span className="text-neon-lime">Disponibles</span>
            </h1>
            <p className="text-zinc-400 text-lg md:text-xl mb-8">
              Notre sélection de véhicules premium prêts à être importés pour vous.
              Allemagne, Belgique, Corée du Sud - nous trouvons les meilleures opportunités.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <span className="bg-zinc-900 border border-zinc-800 px-4 py-2 text-zinc-400">
                🇩🇪 Allemagne
              </span>
              <span className="bg-zinc-900 border border-zinc-800 px-4 py-2 text-zinc-400">
                🇧🇪 Belgique
              </span>
              <span className="bg-zinc-900 border border-zinc-800 px-4 py-2 text-zinc-400">
                🇰🇷 Corée du Sud
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Vehicles */}
      {featuredVehicles.length > 0 && (
        <section className="py-12 border-y border-zinc-800 bg-zinc-900/30">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center gap-2 mb-8">
              <Star className="w-5 h-5 text-neon-lime" />
              <h2 className="font-unbounded text-xl font-bold text-white">
                À la Une
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {featuredVehicles.map((vehicle) => (
                <div
                  key={vehicle.id}
                  data-testid={`featured-vehicle-${vehicle.id}`}
                  onClick={() => openVehicleDetail(vehicle)}
                  className="bg-zinc-900 border-2 border-neon-lime/50 hover:border-neon-lime cursor-pointer transition-all group overflow-hidden"
                >
                    <div className="flex flex-col md:flex-row">
                    {/* Image */}
                    <div className="relative w-full md:w-1/2 aspect-video md:aspect-auto">
                      {vehicle.images && vehicle.images.length > 0 ? (
                        <img
                          src={vehicle.images[0]}
                          alt={`${vehicle.brand} ${vehicle.model}`}
                          className={`w-full h-full object-cover ${vehicle.is_sold ? 'opacity-60' : ''}`}
                        />
                      ) : (
                        <div className="w-full h-full min-h-[200px] bg-zinc-800 flex items-center justify-center">
                          <Car className="w-16 h-16 text-zinc-700" />
                        </div>
                      )}
                      {vehicle.is_sold && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="bg-red-600 text-white font-unbounded font-black text-3xl md:text-4xl px-8 py-3 -rotate-12 shadow-2xl border-4 border-white tracking-wider">
                            VENDUE
                          </div>
                        </div>
                      )}
                      <span className="absolute top-3 left-3 bg-neon-lime text-black text-xs font-bold px-3 py-1 flex items-center gap-1">
                        <Star className="w-3 h-3" /> À LA UNE
                      </span>
                      {vehicle.is_good_deal && !vehicle.is_sold && (
                        <span
                          data-testid={`good-deal-badge-featured-${vehicle.id}`}
                          className="absolute top-3 right-3 bg-orange-500/95 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 flex items-center gap-1 shadow-lg backdrop-blur-sm"
                        >
                          <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                          Bonne affaire
                        </span>
                      )}
                      {vehicle.images && vehicle.images.length > 1 && (
                        <span className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2 py-1 flex items-center gap-1">
                          <ImageIcon className="w-3 h-3" />
                          {vehicle.images.length} photos
                        </span>
                      )}
                    </div>

                    {/* Content */}
                    <div className="w-full md:w-1/2 p-6 flex flex-col justify-between">
                      <div>
                        <h3 className="font-unbounded text-2xl font-bold text-white mb-2">
                          {vehicle.brand} {vehicle.model}
                        </h3>
                        <p className="text-neon-lime text-3xl font-bold mb-4">
                          {formatPrice(vehicle.price)}
                        </p>

                        <div className="grid grid-cols-2 gap-3 text-sm mb-4">
                          <div className="flex items-center gap-2 text-zinc-400">
                            <Calendar className="w-4 h-4" />
                            <span>{vehicle.year}</span>
                          </div>
                          {vehicle.mileage && (
                            <div className="flex items-center gap-2 text-zinc-400">
                              <Gauge className="w-4 h-4" />
                              <span>{vehicle.mileage.toLocaleString()} km</span>
                            </div>
                          )}
                          {vehicle.power && (
                            <div className="flex items-center gap-2 text-zinc-400">
                              <Zap className="w-4 h-4" />
                              <span>{vehicle.power}</span>
                            </div>
                          )}
                          {vehicle.fuel_type && (
                            <div className="flex items-center gap-2 text-zinc-400">
                              <Fuel className="w-4 h-4" />
                              <span className="capitalize">{vehicle.fuel_type}</span>
                            </div>
                          )}
                        </div>

                        {vehicle.description && (
                          <p className="text-zinc-500 text-sm line-clamp-2 mb-4">
                            {vehicle.description}
                          </p>
                        )}
                      </div>

                      <Button className="w-full bg-neon-lime text-black font-bold group-hover:bg-white transition-colors">
                        Voir les détails
                      </Button>
                      <Button
                        variant="outline"
                        onClick={(e) => handleShare(vehicle, e)}
                        data-testid={`share-featured-${vehicle.id}`}
                        className="w-full mt-2 border-zinc-700 text-white hover:bg-zinc-800"
                      >
                        <Share2 className="w-4 h-4 mr-2" /> Partager
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Vehicles Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-unbounded text-xl font-bold text-white">
              {featuredVehicles.length > 0 ? "Autres véhicules" : "Nos véhicules"}
              <span className="text-zinc-500 font-normal text-sm ml-2">
                ({otherVehicles.length > 0 ? otherVehicles.length : vehicles.length} disponible{(otherVehicles.length > 0 ? otherVehicles.length : vehicles.length) !== 1 ? "s" : ""})
              </span>
            </h2>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-2 border-neon-lime border-t-transparent rounded-full animate-spin" />
            </div>
          ) : vehicles.length === 0 ? (
            <div className="text-center py-20 bg-zinc-900/50 border border-zinc-800">
              <Car className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">
                Aucun véhicule disponible
              </h3>
              <p className="text-zinc-500 mb-6 max-w-md mx-auto">
                Notre stock évolue régulièrement. Contactez-nous pour nous faire part de vos critères de recherche.
              </p>
              <Link to="/contact">
                <Button className="bg-neon-lime text-black font-bold">
                  Nous contacter
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(featuredVehicles.length > 0 ? otherVehicles : vehicles).map((vehicle) => (
                <div
                  key={vehicle.id}
                  data-testid={`vehicle-card-${vehicle.id}`}
                  onClick={() => openVehicleDetail(vehicle)}
                  className="bg-zinc-900 border border-zinc-800 hover:border-neon-lime/50 cursor-pointer transition-all group overflow-hidden"
                >
                  {/* Image */}
                  <div className="relative aspect-video bg-zinc-800">
                    {vehicle.images && vehicle.images.length > 0 ? (
                      <img
                        src={vehicle.images[0]}
                        alt={`${vehicle.brand} ${vehicle.model}`}
                        className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ${vehicle.is_sold ? 'opacity-60' : ''}`}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Car className="w-12 h-12 text-zinc-700" />
                      </div>
                    )}
                    {vehicle.is_sold && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="bg-red-600 text-white font-unbounded font-black text-2xl md:text-3xl px-6 py-2 -rotate-12 shadow-2xl border-4 border-white tracking-wider">
                          VENDUE
                        </div>
                      </div>
                    )}
                    {vehicle.images && vehicle.images.length > 1 && (
                      <span className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 flex items-center gap-1">
                        <ImageIcon className="w-3 h-3" />
                        {vehicle.images.length}
                      </span>
                    )}
                    {vehicle.is_good_deal && !vehicle.is_sold && (
                      <span
                        data-testid={`good-deal-badge-${vehicle.id}`}
                        className="absolute top-2 right-2 bg-orange-500/95 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 flex items-center gap-1 shadow-lg backdrop-blur-sm"
                      >
                        <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                        Bonne affaire
                      </span>
                    )}
                    {vehicle.origin && (
                      <span className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1">
                        {vehicle.origin === "Allemagne" && "🇩🇪"}
                        {vehicle.origin === "Belgique" && "🇧🇪"}
                        {vehicle.origin === "Corée du Sud" && "🇰🇷"}
                        {vehicle.origin === "Italie" && "🇮🇹"}
                        {vehicle.origin === "Espagne" && "🇪🇸"}
                        {vehicle.origin === "Pays-Bas" && "🇳🇱"}
                        {vehicle.origin === "France" && "🇫🇷"}{" "}
                        {vehicle.origin}
                      </span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-unbounded text-lg font-bold text-white group-hover:text-neon-lime transition-colors">
                          {vehicle.brand} {vehicle.model}
                        </h3>
                        <p className="text-zinc-500 text-sm">
                          {vehicle.year} • {vehicle.power || "N/A"}
                        </p>
                      </div>
                    </div>

                    <p className="text-neon-lime font-bold text-2xl mb-3">
                      {formatPrice(vehicle.price)}
                    </p>

                    <div className="flex flex-wrap gap-2 text-xs text-zinc-500">
                      {vehicle.mileage && (
                        <span className="flex items-center gap-1 bg-zinc-800 px-2 py-1">
                          <Gauge className="w-3 h-3" />
                          {vehicle.mileage.toLocaleString()} km
                        </span>
                      )}
                      {vehicle.fuel_type && (
                        <span className="flex items-center gap-1 bg-zinc-800 px-2 py-1 capitalize">
                          <Fuel className="w-3 h-3" />
                          {vehicle.fuel_type}
                        </span>
                      )}
                      {vehicle.transmission && (
                        <span className="flex items-center gap-1 bg-zinc-800 px-2 py-1 capitalize">
                          <Settings2 className="w-3 h-3" />
                          {vehicle.transmission}
                        </span>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={(e) => handleShare(vehicle, e)}
                      data-testid={`share-card-${vehicle.id}`}
                      aria-label="Partager ce véhicule"
                      className="mt-4 w-full flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-wider border border-zinc-800 hover:border-neon-lime text-zinc-400 hover:text-neon-lime py-2 transition-colors"
                    >
                      <Share2 className="w-3.5 h-3.5" />
                      Partager ce véhicule
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-zinc-900 border-t border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-unbounded text-2xl md:text-3xl font-bold text-white mb-4">
            Vous ne trouvez pas votre bonheur ?
          </h2>
          <p className="text-zinc-400 text-lg mb-8">
            Nous recherchons le véhicule de vos rêves partout en Europe et en Corée du Sud.
            Décrivez-nous vos critères et nous nous occupons du reste.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-neon-lime text-black font-bold px-8 py-3">
                Demander une recherche personnalisée
              </Button>
            </Link>
            <Link to="/simulateur">
              <Button variant="outline" className="border-zinc-700 text-white px-8 py-3">
                Simuler les coûts d'import
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Vehicle Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={closeVehicleDetail}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-4xl max-h-[95vh] overflow-y-auto p-0">
          {selectedVehicle && (
            <>
              {/* Image Gallery */}
              <div className="relative aspect-video bg-zinc-800">
                {selectedVehicle.images && selectedVehicle.images.length > 0 ? (
                  <>
                    <img
                      src={selectedVehicle.images[currentImageIndex]}
                      alt={`${selectedVehicle.brand} ${selectedVehicle.model}`}
                      className="w-full h-full object-cover cursor-pointer"
                      onClick={() => setImageGalleryOpen(true)}
                    />
                    {selectedVehicle.images.length > 1 && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            prevImage();
                          }}
                          className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 transition-colors"
                        >
                          <ChevronLeft className="w-6 h-6" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            nextImage();
                          }}
                          className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 transition-colors"
                        >
                          <ChevronRight className="w-6 h-6" />
                        </button>
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                          {selectedVehicle.images.map((_, index) => (
                            <button
                              key={index}
                              onClick={() => setCurrentImageIndex(index)}
                              className={`w-2 h-2 rounded-full transition-colors ${
                                index === currentImageIndex
                                  ? "bg-neon-lime"
                                  : "bg-white/50 hover:bg-white/70"
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Car className="w-20 h-20 text-zinc-700" />
                  </div>
                )}

                {/* Thumbnails */}
                {selectedVehicle.images && selectedVehicle.images.length > 1 && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                    <div className="flex gap-2 overflow-x-auto">
                      {selectedVehicle.images.map((img, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`flex-shrink-0 w-16 h-12 border-2 overflow-hidden ${
                            index === currentImageIndex
                              ? "border-neon-lime"
                              : "border-transparent opacity-60 hover:opacity-100"
                          }`}
                        >
                          <img
                            src={img}
                            alt={`Thumbnail ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
                  <div>
                    <h2 className="font-unbounded text-2xl md:text-3xl font-bold text-white">
                      {selectedVehicle.brand} {selectedVehicle.model}
                    </h2>
                    <p className="text-zinc-500 text-lg">
                      {selectedVehicle.year} • {selectedVehicle.origin || "Europe"}
                    </p>
                  </div>
                  <p className="text-neon-lime font-bold text-3xl md:text-4xl">
                    {formatPrice(selectedVehicle.price)}
                  </p>
                </div>

                {/* Share toolbar */}
                <div className="flex flex-wrap items-center gap-2 mb-6 p-3 border border-zinc-800 bg-zinc-950/50">
                  <span className="text-xs uppercase tracking-widest text-zinc-500 mr-2">
                    Partager
                  </span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyLink(selectedVehicle)}
                    data-testid="modal-copy-link"
                    className="border-zinc-700 text-white hover:bg-zinc-800"
                  >
                    <Copy className="w-3.5 h-3.5 mr-2" /> Copier le lien
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => shareWhatsApp(selectedVehicle)}
                    data-testid="modal-share-whatsapp"
                    className="border-[#25D366]/50 text-[#25D366] hover:bg-[#25D366]/10"
                  >
                    <MessageSquare className="w-3.5 h-3.5 mr-2" /> WhatsApp
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => shareFacebook(selectedVehicle)}
                    data-testid="modal-share-facebook"
                    className="border-[#1877F2]/50 text-[#1877F2] hover:bg-[#1877F2]/10"
                  >
                    <Facebook className="w-3.5 h-3.5 mr-2" /> Facebook
                  </Button>
                  {typeof navigator !== "undefined" && navigator.share && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleShare(selectedVehicle)}
                      data-testid="modal-native-share"
                      className="border-neon-lime/50 text-neon-lime hover:bg-neon-lime/10"
                    >
                      <Share2 className="w-3.5 h-3.5 mr-2" /> Partager…
                    </Button>
                  )}
                </div>

                {/* Specs Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  {selectedVehicle.mileage && (
                    <div className="bg-zinc-800 p-4">
                      <div className="flex items-center gap-2 text-zinc-500 text-sm mb-1">
                        <Gauge className="w-4 h-4" />
                        Kilométrage
                      </div>
                      <p className="text-white font-bold">
                        {selectedVehicle.mileage.toLocaleString()} km
                      </p>
                    </div>
                  )}
                  {selectedVehicle.fuel_type && (
                    <div className="bg-zinc-800 p-4">
                      <div className="flex items-center gap-2 text-zinc-500 text-sm mb-1">
                        <Fuel className="w-4 h-4" />
                        Carburant
                      </div>
                      <p className="text-white font-bold capitalize">
                        {selectedVehicle.fuel_type}
                      </p>
                    </div>
                  )}
                  {selectedVehicle.transmission && (
                    <div className="bg-zinc-800 p-4">
                      <div className="flex items-center gap-2 text-zinc-500 text-sm mb-1">
                        <Settings2 className="w-4 h-4" />
                        Transmission
                      </div>
                      <p className="text-white font-bold capitalize">
                        {selectedVehicle.transmission}
                      </p>
                    </div>
                  )}
                  {selectedVehicle.power && (
                    <div className="bg-zinc-800 p-4">
                      <div className="flex items-center gap-2 text-zinc-500 text-sm mb-1">
                        <Zap className="w-4 h-4" />
                        Puissance
                      </div>
                      <p className="text-white font-bold">{selectedVehicle.power}</p>
                    </div>
                  )}
                  {selectedVehicle.color && (
                    <div className="bg-zinc-800 p-4">
                      <div className="flex items-center gap-2 text-zinc-500 text-sm mb-1">
                        <Palette className="w-4 h-4" />
                        Couleur
                      </div>
                      <p className="text-white font-bold">{selectedVehicle.color}</p>
                    </div>
                  )}
                  {selectedVehicle.origin && (
                    <div className="bg-zinc-800 p-4">
                      <div className="flex items-center gap-2 text-zinc-500 text-sm mb-1">
                        <MapPin className="w-4 h-4" />
                        Origine
                      </div>
                      <p className="text-white font-bold">{selectedVehicle.origin}</p>
                    </div>
                  )}
                </div>

                {/* Description */}
                {selectedVehicle.description && (
                  <div className="mb-6">
                    <h3 className="font-unbounded text-lg font-bold text-white mb-3">
                      Description
                    </h3>
                    <p className="text-zinc-400 whitespace-pre-line">
                      {selectedVehicle.description}
                    </p>
                  </div>
                )}

                {/* Features */}
                {selectedVehicle.features && selectedVehicle.features.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-unbounded text-lg font-bold text-white mb-3">
                      Équipements
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {selectedVehicle.features.map((feature, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 text-zinc-400 text-sm"
                        >
                          <Check className="w-4 h-4 text-neon-lime flex-shrink-0" />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* CTA */}
                <div className="border-t border-zinc-800 pt-6">
                  {selectedVehicle.is_sold ? (
                    <div className="bg-red-600/10 border-2 border-red-600/50 p-6 text-center">
                      <div className="inline-block bg-red-600 text-white font-unbounded font-black text-xl px-6 py-2 mb-4 tracking-wider">
                        VENDUE
                      </div>
                      <p className="text-white text-lg font-bold mb-2">
                        Ce véhicule a trouvé son propriétaire
                      </p>
                      <p className="text-zinc-400 text-sm mb-4">
                        Vous cherchez un modèle similaire ? Nous pouvons le trouver pour vous.
                      </p>
                      <Link to="/contact">
                        <Button className="bg-neon-lime text-black font-bold" data-testid="sold-contact-btn">
                          Demander une recherche personnalisée
                        </Button>
                      </Link>
                    </div>
                  ) : !showContactForm ? (
                    <>
                      <p className="text-zinc-500 text-sm mb-4">
                        Intéressé par ce véhicule ? Contactez-nous pour plus d'informations ou pour réserver.
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <Button 
                          onClick={() => setShowContactForm(true)}
                          className="flex-1 min-w-[200px] bg-neon-lime text-black font-bold"
                          data-testid="request-info-btn"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Demander des infos
                        </Button>
                        <a href="tel:+33768013271" className="flex-1 min-w-[140px]">
                          <Button variant="outline" className="w-full border-zinc-700 text-white">
                            <Phone className="w-4 h-4 mr-2" />
                            Appeler
                          </Button>
                        </a>
                        <a
                          href={`https://wa.me/33768013271?text=Bonjour, je suis intéressé par le ${selectedVehicle.brand} ${selectedVehicle.model} ${selectedVehicle.year} à ${formatPrice(selectedVehicle.price)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 min-w-[140px]"
                        >
                          <Button className="w-full bg-[#25D366] text-white font-bold">
                            <MessageCircle className="w-4 h-4 mr-2" />
                            WhatsApp
                          </Button>
                        </a>
                      </div>
                    </>
                  ) : (
                    <form onSubmit={handleContactSubmit} className="space-y-4">
                      <div className="bg-zinc-800/50 border border-neon-lime/30 p-3 mb-4">
                        <p className="text-neon-lime text-sm font-bold">
                          Demande d'information pour :
                        </p>
                        <p className="text-white">
                          {selectedVehicle.brand} {selectedVehicle.model} {selectedVehicle.year} - {formatPrice(selectedVehicle.price)}
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="contact-name" className="text-white flex items-center gap-2">
                            <User className="w-4 h-4 text-neon-lime" />
                            Nom <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="contact-name"
                            value={contactForm.name}
                            onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                            placeholder="Votre nom"
                            className="bg-zinc-800 border-zinc-700 text-white"
                            required
                            data-testid="contact-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="contact-phone" className="text-white flex items-center gap-2">
                            <Phone className="w-4 h-4 text-neon-lime" />
                            Téléphone <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="contact-phone"
                            value={contactForm.phone}
                            onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                            placeholder="06 XX XX XX XX"
                            className="bg-zinc-800 border-zinc-700 text-white"
                            required
                            data-testid="contact-phone-input"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-white flex items-center gap-2">
                          <Package className="w-4 h-4 text-neon-lime" />
                          Prestation souhaitée
                        </Label>
                        <Select
                          value={contactForm.prestation}
                          onValueChange={(value) => setContactForm({ ...contactForm, prestation: value })}
                        >
                          <SelectTrigger 
                            className="bg-zinc-800 border-zinc-700 text-white"
                            data-testid="contact-prestation-select"
                          >
                            <SelectValue placeholder="Sélectionnez une prestation" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            {PRESTATIONS.filter(p => p.value).map((prestation) => (
                              <SelectItem 
                                key={prestation.value} 
                                value={prestation.value}
                                className="text-white hover:bg-zinc-800"
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">{prestation.label}</span>
                                  <span className="text-xs text-zinc-500">{prestation.description}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="contact-email" className="text-white flex items-center gap-2">
                          <Mail className="w-4 h-4 text-zinc-500" />
                          Email (optionnel)
                        </Label>
                        <Input
                          id="contact-email"
                          type="email"
                          value={contactForm.email}
                          onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                          placeholder="votre@email.com"
                          className="bg-zinc-800 border-zinc-700 text-white"
                          data-testid="contact-email-input"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="contact-message" className="text-white">
                          Message (optionnel)
                        </Label>
                        <Textarea
                          id="contact-message"
                          value={contactForm.message}
                          onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                          placeholder="Vos questions sur ce véhicule..."
                          rows={2}
                          className="bg-zinc-800 border-zinc-700 text-white"
                          data-testid="contact-message-input"
                        />
                      </div>
                      
                      <div className="flex gap-3 pt-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowContactForm(false)}
                          className="flex-1 border-zinc-700 text-white"
                        >
                          Annuler
                        </Button>
                        <Button
                          type="submit"
                          disabled={submitting}
                          className="flex-1 bg-neon-lime text-black font-bold"
                          data-testid="submit-contact-btn"
                        >
                          {submitting ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Envoi...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Envoyer ma demande
                            </>
                          )}
                        </Button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Full Screen Image Gallery */}
      <Dialog open={imageGalleryOpen} onOpenChange={setImageGalleryOpen}>
        <DialogContent className="bg-black border-none max-w-[95vw] max-h-[95vh] p-0">
          {selectedVehicle?.images && (
            <div className="relative w-full h-[90vh] flex items-center justify-center">
              <button
                onClick={() => setImageGalleryOpen(false)}
                className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white p-2 z-10"
              >
                <X className="w-6 h-6" />
              </button>
              <img
                src={selectedVehicle.images[currentImageIndex]}
                alt={`${selectedVehicle.brand} ${selectedVehicle.model}`}
                className="max-w-full max-h-full object-contain"
              />
              {selectedVehicle.images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3"
                  >
                    <ChevronLeft className="w-8 h-8" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3"
                  >
                    <ChevronRight className="w-8 h-8" />
                  </button>
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 px-4 py-2 text-white">
                    {currentImageIndex + 1} / {selectedVehicle.images.length}
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AvailableVehiclesPage;
